import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import services from '../config/services.json';
import genres from '../config/genres.json';
import { tmdb, posterUrl } from '../lib/tmdb.js';
import MovieModal from '../components/MovieModal.jsx';

// Kiosk mode — fullscreen, touch-friendly, idle screensaver.
// Designed for an iPad mounted in the living room. Bookmark
// homeblockbuster.com/kiosk and walk up to it.
const IDLE_MS = 30_000;   // go to screensaver after 30s of no interaction
const SLIDE_MS = 8_000;   // cycle screensaver poster every 8s

export default function Kiosk() {
  const [aisles, setAisles] = useState({});      // { aisleId: [movies] }
  const [loading, setLoading] = useState(true);
  const [openRef, setOpenRef] = useState(null);
  const [idle, setIdle] = useState(false);
  const [slide, setSlide] = useState(0);
  const idleTimer = useRef(null);

  const providerIds = useMemo(
    () => services.services.filter((s) => s.active).map((s) => s.tmdbProviderId).join('|'),
    []
  );

  // Load every genre aisle on mount.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all(genres.aisles.map((a) => {
      const params = { providers: providerIds, sort: 'popularity.desc', media: 'all' };
      if (a.tmdbGenreId) params.genre = a.tmdbGenreId;
      if (a.filterRule === 'non-english') params.lang = 'ja';
      return tmdb.discover(params).then((d) => [a.id, d.results || []]);
    })).then((entries) => {
      if (!alive) return;
      setAisles(Object.fromEntries(entries));
      setLoading(false);
    });
    return () => { alive = false; };
  }, [providerIds]);

  // All posters flattened for the screensaver.
  const allPosters = useMemo(() => {
    const set = new Set();
    const out = [];
    for (const list of Object.values(aisles)) for (const m of list) {
      const k = `${m.media_type}:${m.id}`;
      if (set.has(k)) continue;
      set.add(k);
      if (m.poster_path) out.push(m);
    }
    return out;
  }, [aisles]);

  // Reset idle timer on any interaction.
  function bumpIdle() {
    if (idle) setIdle(false);
    clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setIdle(true), IDLE_MS);
  }
  useEffect(() => {
    bumpIdle();
    const events = ['mousemove', 'touchstart', 'keydown', 'scroll'];
    events.forEach((e) => window.addEventListener(e, bumpIdle, { passive: true }));
    return () => {
      events.forEach((e) => window.removeEventListener(e, bumpIdle));
      clearTimeout(idleTimer.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Screensaver: rotate to next poster every SLIDE_MS.
  useEffect(() => {
    if (!idle || allPosters.length === 0) return;
    const t = setInterval(() => {
      setSlide((s) => (s + 1) % allPosters.length);
    }, SLIDE_MS);
    return () => clearInterval(t);
  }, [idle, allPosters.length]);

  if (idle && allPosters.length > 0) {
    return <Screensaver posters={allPosters} index={slide} />;
  }

  return (
    <div className="hb-kiosk">
      <header className="hb-kiosk__header">
        <Link to="/" className="hb-logo">
          <span className="hb-logo__ticket">HOME</span>
          <span className="hb-logo__word">BLOCKBUSTER</span>
        </Link>
        <span className="hb-kiosk__tagline">Tap a film to see what's on tonight</span>
      </header>

      {loading && Object.keys(aisles).length === 0 ? (
        <div className="hb-empty">Loading the floor…</div>
      ) : (
        genres.aisles.map((a) => {
          const list = (aisles[a.id] || []).slice(0, 14);
          if (list.length === 0) return null;
          return (
            <section key={a.id} className="hb-kiosk__shelf">
              <h2 className="hb-kiosk__shelf-title">{a.name}</h2>
              <div className="hb-kiosk__rail">
                {list.map((m) => (
                  <button
                    key={`${m.media_type}:${m.id}`}
                    type="button"
                    className="hb-kiosk__poster"
                    onClick={() => setOpenRef({ tmdb_id: m.id, media_type: m.media_type || 'movie' })}
                  >
                    <img src={posterUrl(m.poster_path, 'w500')} alt="" loading="lazy" />
                  </button>
                ))}
              </div>
            </section>
          );
        })
      )}

      {openRef && <MovieModal movieId={openRef} onClose={() => setOpenRef(null)} />}
    </div>
  );
}

function Screensaver({ posters, index }) {
  const m = posters[index];
  const nextM = posters[(index + 1) % posters.length];
  return (
    <div className="hb-screensaver" onClick={() => window.dispatchEvent(new Event('mousemove'))}>
      <img
        key={m.id}
        className="hb-screensaver__poster hb-screensaver__poster--enter"
        src={posterUrl(m.poster_path, 'w780')}
        alt=""
      />
      {/* Preload the next one so the transition is instant */}
      <img
        key={'next' + nextM.id}
        className="hb-screensaver__preload"
        src={posterUrl(nextM.poster_path, 'w780')}
        alt=""
        aria-hidden="true"
      />
      <div className="hb-screensaver__bar" aria-hidden="true">
        <div className="hb-screensaver__filmstrip" />
      </div>
      <div className="hb-screensaver__tap">Tap to enter</div>
    </div>
  );
}
